import type { VectorFn } from "~/lib/types";
import type { NodeDefinition } from "~/nodes/types";

/**
 * Combines two curves axis-wise. With only one side connected the curve passes
 * through unchanged, which is what the rete version did.
 */
function combine(
  f1: VectorFn | undefined,
  f2: VectorFn | undefined,
  op: (a: number, b: number) => number,
): VectorFn | undefined {
  if (f1 && !f2) return f1;
  if (f2 && !f1) return f2;
  if (!f1 || !f2) return undefined;

  return {
    x: (t) => op(f1.x(t), f2.x(t)),
    y: (t) => op(f1.y(t), f2.y(t)),
    z: (t) => op(f1.z(t), f2.z(t)),
  };
}

function binaryVectorNode(
  type: string,
  label: string,
  op: (a: number, b: number) => number,
): NodeDefinition {
  return {
    type,
    label,
    category: "Curves",
    inputs: [
      { key: "f1", label: "F1", socket: "vector" },
      { key: "f2", label: "F2", socket: "vector" },
    ],
    outputs: [{ key: "out", label: "Result", socket: "vector" }],
    controls: [],
    defaults: {},

    compute(ctx) {
      return {
        out: combine(
          ctx.connected<VectorFn>("f1"),
          ctx.connected<VectorFn>("f2"),
          op,
        ),
      };
    },
  };
}

export const addFunctionsNode = binaryVectorNode(
  "addFunctions",
  "Add Functions",
  (a, b) => a + b,
);

export const multiplyFunctionsNode = binaryVectorNode(
  "multiplyFunctions",
  "Multiply Functions",
  (a, b) => a * b,
);
